import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { OrderDirection } from '../../../../gen-src/queries/globalTypes';
import { getSingleParamValue, paramValuesMapToSearch, searchToParamValuesMap, setParamValues } from '../../urls/UrlSearchFunctions';
import { UrlSearch, UrlSearchProvider } from '../../urls/UrlSearchProvider';
import { isOrderByEqual, OrderBy } from './OrderBy';
import { buildDefaultOrder, DefaultDirections, SortProviderProps } from './SortProviderProps';

export function getFieldAndDirectionFromQueryParam<OrderFields extends string>(
  queryParamValue: string,
  validFields: DefaultDirections<OrderFields>,
): OrderBy<OrderFields> | null {
  const parts = queryParamValue.split(':');
  if (parts.length !== 2) {
    return null;
  }

  const validField = validateValue(parts[0], validFields);
  const validDirection = validateValue(parts[1], OrderDirection);

  if (validField && validDirection) {
    return { field: validField, direction: validDirection } as OrderBy<OrderFields>;
  } else {
    return null;
  }
}

export function constructNewSearchForSortOrder<OrderFields extends string>(search: string, sortOrder: OrderBy<OrderFields> | null): string {
  const paramValuesMap = searchToParamValuesMap(search);
  if (paramValuesMap[CatalogUrlParamKeys.order]) {
    delete paramValuesMap[CatalogUrlParamKeys.order];
  }
  if (sortOrder) {
    const encode = false; // don't encode the colon
    setParamValues(paramValuesMap, CatalogUrlParamKeys.order, [constructOrderParamValue(sortOrder)], encode);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export function constructOrderParamValue(sortOrder: OrderBy<string>) {
  return `${sortOrder.field.toLowerCase()}:${sortOrder.direction.toLowerCase()}`;
}

/**
 * Validates that the value is valid and returns the valid/normalized version of the value, or else returns null.
 * We rely on the fact that GraphQL generates enum types where the keys and values are the same,
 * and that they're always in all caps.
 */
export function validateValue<F extends string>(value: string, validFields: DefaultDirections<F>): F | null {
  // Because all the values in the GraphQL enums are in all caps, we "normalize" the user input to uppercase
  const uppercaseValue = value.toUpperCase();
  if (uppercaseValue in validFields) {
    return uppercaseValue as F;
  } else {
    return null;
  }
}

export class UrlSortProvider<OrderFields extends string>
  extends React.Component<SortProviderProps<OrderFields>> {

  public render() {
    return (
      <UrlSearchProvider>
        {urlSearch => <UrlSortProviderInternal urlSearch={urlSearch} {...this.props} />}
      </UrlSearchProvider>
    );
  }
}

export type UrlSortProviderInternalProps<OrderFields extends string> = SortProviderProps<OrderFields> & {
  readonly urlSearch: UrlSearch;
};

export class UrlSortProviderInternal<OrderFields extends string>
  extends React.Component<UrlSortProviderInternalProps<OrderFields>> {

  // tslint:disable:typedef
  private memoizedGetFieldAndDirectionFromQueryParam = defaultMemoize(getFieldAndDirectionFromQueryParam);
  private memoizedBuildDefaultOrder = defaultMemoize(buildDefaultOrder);
  // tslint:enable:typedef

  public render() {
    return this.props.children({
      orderBy: this.getParsedOrderOrDefault(),
      updateSortOrder: this.updateOrderInUrl,
    });
  }

  public componentDidMount() {
    this.clearOrderInUrlIfInvalidOrDefault();
  }

  public componentDidUpdate() {
    this.clearOrderInUrlIfInvalidOrDefault();
  }

  private getDefaultOrder() {
    return this.memoizedBuildDefaultOrder(this.props.defaultField, this.props.validOrderFieldsWithDefaultDirections);
  }

  private getParsedOrderOrDefault = () => {
    const sortQueryParamValue = getSingleParamValue(this.props.urlSearch.value, CatalogUrlParamKeys.order);
    const defaultDirections = this.props.validOrderFieldsWithDefaultDirections;
    const parsedSort = this.memoizedGetFieldAndDirectionFromQueryParam(sortQueryParamValue, defaultDirections);
    return parsedSort || this.getDefaultOrder();
  }

  private clearOrderInUrlIfInvalidOrDefault = () => {
    const parsedOrderOrDefault = this.getParsedOrderOrDefault();

    if (isOrderByEqual(parsedOrderOrDefault, this.getDefaultOrder())) {
      const newSearch = constructNewSearchForSortOrder(this.props.urlSearch.value, null);
      this.props.urlSearch.update(newSearch);
    }
  }

  private updateOrderInUrl = (sortField: string) => {
    const validatedSortField = validateValue(sortField, this.props.validOrderFieldsWithDefaultDirections);
    if (validatedSortField === null) {
      return;
    }

    const currentOrder = this.getParsedOrderOrDefault();
    let newOrder: OrderBy<OrderFields>;

    if (currentOrder && currentOrder.field === sortField) {
      newOrder = {
        direction: currentOrder.direction === OrderDirection.ASC ? OrderDirection.DESC : OrderDirection.ASC,
        field: validatedSortField,
      };
    } else {
      newOrder = {
        direction: this.props.validOrderFieldsWithDefaultDirections[validatedSortField],
        field: validatedSortField,
      };
    }

    const newSearch = constructNewSearchForSortOrder(this.props.urlSearch.value, newOrder);
    this.props.urlSearch.update(newSearch);
  }
}
